import { useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Checkbox,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Label,
  Text,
  type IconName,
} from "@salestrak/ui";
import { WorkflowSelector } from "#app/components/entity-selectors";
import { useWorkspaceData, type WorkspaceStage } from "#app/lib/data/workspace-data-provider";
import { EntityTag } from "#app/components/entity";
import type { Omittable } from "@salestrak/ui";

const EMPTY_STAGES: WorkspaceStage[] = [];

type WorkflowStageValue = {
  workflowIds: string[];
  stageIds: string[];
};

type WorkflowStageInputProps = Omittable<{
  value: unknown;
  onChange: (value: unknown) => void;
  icon?: IconName;
  /** Stretch the trigger to fill its container. */
  fill?: boolean;
}>;

/**
 * Composite filter: select workflows, then pick stages within each.
 */
export function WorkflowStageInput({ value, onChange, icon, fill }: WorkflowStageInputProps) {
  const parsed = (value as WorkflowStageValue) ?? { workflowIds: [], stageIds: [] };
  const { workflowIds, stageIds } = parsed;
  const { stageMap } = useWorkspaceData();

  const handleWorkflowChange = useCallback(
    (ids: string[]) => {
      const idSet = new Set(ids);
      const nextStageIds = stageIds.filter((sid) => {
        const wfId = stageMap.get(sid)?.workflowId;
        return wfId ? idSet.has(wfId) : false;
      });
      onChange({ workflowIds: ids, stageIds: nextStageIds });
    },
    [stageIds, onChange, stageMap],
  );

  const toggleStage = useCallback(
    (stageId: string) => {
      const next = stageIds.includes(stageId)
        ? stageIds.filter((id) => id !== stageId)
        : [...stageIds, stageId];
      onChange({ workflowIds, stageIds: next });
    },
    [workflowIds, stageIds, onChange],
  );

  return (
    <Box gap="md">
      <WorkflowSelector
        value={workflowIds}
        onValueChange={handleWorkflowChange}
        maxSelections={0}
      >
        <EntitySelectorTrigger
          placeholder="Select workflows..."
          size="sm"
          chips
          icon={icon}
          fill={fill}
        />
        <EntitySelectorContent fit>
          <EntitySelectorInput />
          <EntitySelectorPool value="workflows" />
          <EntitySelectorEmpty>No workflows found</EntitySelectorEmpty>
        </EntitySelectorContent>
      </WorkflowSelector>

      {workflowIds.length > 0 && (
        <Box paddingLeft="md">
          {/* The accordion panel animates its own height and no call site
              can turn that off. */}
          <Accordion multiple defaultValue={workflowIds}>
            {workflowIds.map((wfId) => (
              <WorkflowStageSection
                key={wfId}
                workflowId={wfId}
                selectedStageIds={stageIds}
                onToggleStage={toggleStage}
              />
            ))}
          </Accordion>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Per-workflow stage section
// ---------------------------------------------------------------------------

type WorkflowStageSectionProps = {
  workflowId: string;
  selectedStageIds: string[];
  onToggleStage: (stageId: string) => void;
};

function WorkflowStageSection({
  workflowId,
  selectedStageIds,
  onToggleStage,
}: WorkflowStageSectionProps) {
  const { workflowMap, stagesByWorkflow } = useWorkspaceData();
  const stages = stagesByWorkflow.get(workflowId) ?? EMPTY_STAGES;
  const workflowName = workflowMap.get(workflowId)?.name ?? "Loading...";

  return (
    <AccordionItem value={workflowId}>
      <AccordionTrigger>
        <Text size="sm" weight="medium">{workflowName}</Text>
      </AccordionTrigger>
      <AccordionPanel>
        {/* `Label` is inline-flex; a block parent would flow the rows inline. */}
        <Box direction="column" gap="sm">
          {stages.map((stage) => (
            // Checkbox has no label slot; the `Label` wraps both parts.
            <Label key={stage.stageId}>
              <Checkbox
                checked={selectedStageIds.includes(stage.stageId)}
                onCheckedChange={() => onToggleStage(stage.stageId)}
              />
              <EntityTag
                entity={{
                  kind: "stage",
                  id: stage.stageId,
                  stage,
                  workflowId,
                }}
                size="sm"
                weight="regular"
                interactive={false}
              />
            </Label>
          ))}
          {stages.length === 0 && (
            <Text size="xs" variant="muted">Loading stages...</Text>
          )}
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );
}
